import { RecursivePartial } from '../index'
import { Company } from './companies'
import {
  AdrClassSlug,
  CargoTypeSlug,
  CurrencySlug,
  ExtraEquipmentSlug,
  LoadingTypeSlug,
  PaymentConditionSlug,
  PaymentMethodSlug,
  PointTypeSlug, RouteStartDateOffsetDays,
  ShippingModeSlug,
  TransportTypeSlug,
  TruckBodySlug
} from './knowledge/exchange'
import { Profile } from './profiles'
import { GeoJson, ID, PointGeoJson } from '../scalars'

export type CreateOfferResponse = Offer
export type UpdateOfferResponse = Offer
export type FlexiblePolyline = string

export enum CreationMethod {
  MANUAL = 'manual'
}

export enum MileageFormat {
  KM = 'km',
  MILES = 'miles'
}

export enum OfferStatus {
  PUBLISHED = 'published',
  NOT_PUBLISHED = 'not_published',
  CLOSED = 'closed'
}

export enum AssigneeContactMethod {
  PHONE = 'phone',
  EMAIL = 'email',
  CHAT = 'chat'
}

export enum OfferType {
  CARGO = 'cargo',
  TRANSPORT = 'transport'
}

export interface BaseOffer {
  _id: string
  assignee_users: AssigneeContact[] | null
  attachments: string[] | null
  autoclose_at: string
  closed_at: string
  company_id: Company['id'] | null
  complaints: {
    created_at: string
    user_id: string
  }[]
  complaints_count: number
  created_at: string
  creation_method: CreationMethod
  creation_profile_id: Profile['id']
  deleted_at: string | null
  description: string | null
  geo_process_state?: GeoProcessState
  is_autoclosed: boolean
  is_public: boolean
  is_publication_autocanceled: boolean
  last_matched_at: string
  // map_routes: deprecated
  matches_exact?: string[] | null
  matches_exact_count?: number | null
  matches_partial?: string[] | null
  matches_partial_count?: number | null
  offer_name: string
  offer_type: OfferType
  organization: Organization | null
  payment: Payment
  publication_lifetime: PublicationLifetime
  bids_info: BidsInfo
  private_partners?: string[] | null
  publication_autocancel_at: string
  publication_autocancel_type: 'time_expiration' | 'assignee_removed' | 'moderator'
  published_at: string
  route: RoutePoint[]
  route_way?: RouteWay
  status: OfferStatus
  status_changed_at: string
  update_visibility_at: string
  updated_at: string
  views_count: number
  watching_now_count?: number
}

export interface CargoOffer extends BaseOffer {
  cargo_units: CargoUnit[] | null
  transportation_requirement: TransportationRequirement
}

export interface TransportOffer extends BaseOffer {
  transport: TransportInfo
}

export type Offer = CargoOffer | TransportOffer

export interface GetOfferRequest {
  id: Offer['_id'],
  active_space_company_id: Company['id'] | null
  polygon?: 1
  way_polyline?: 1
}

export interface ExchangePagination {
  sort?: 'asc' | 'desc'
  page?: number
  records?: number
}

export interface CreateOfferRequestBase {
  creation_method: CreationMethod.MANUAL
  organization: { id: ID, name: string } | null
  assignee_users: Array<{
    id: Profile['id']
    contact_methods: { method: AssigneeContactMethod }[]
  }>
  is_public: boolean
  private_partners: ID[]
  offer_type: OfferType
  description: string | null
  attachments: URL[]
  route: Array<Omit<RoutePoint, '_id'>>,

  cargo_units: Array<Omit<CargoUnit, '_id'>> | null
  transportation_requirement: TransportationRequirement | null,
  transport?: TransportInfo
  payment: Partial<Payment>
}

export interface CreateOfferRequest extends CreateOfferRequestBase {
  status: OfferStatus.PUBLISHED | OfferStatus.NOT_PUBLISHED
  // company_id: Company['id'] | null
}

export interface UpdateOfferRequest extends RecursivePartial<Omit<CreateOfferRequest, 'status'>> {
  id: string
  status?: OfferStatus
  deleted_at?: string | null
}

export interface CreateDuplicatesRequest {
  offer: Pick<Offer, '_id'>,
  count: number
}

export interface RoutePointDatetime {
  date: string | null // YYYY-MM-DD
  time: string | null // HH:MM:SS
  datetime?: string // usually exist on offer output
}

export interface RoutePoint {
  _id: string
  end_datetime: RoutePointDatetime | null
  loading_type: LoadingTypeSlug[] | null
  location: {
    osm_id: string
    osm_type: string

    address: {
      title: string | null
      lang: string | null
      country_code: string | null
      country_name: string | null
      county: string | null
      state_name: string | null
      state_code: string | null
      is_ru_us_in: boolean | null
      city: string | null
      district: string | null
      sub_district: string | null
      street: string | null
      postal_code: string | null
      house_number: string | null
      time_zone: string | null
    },
    is_radius: boolean
    radius: number | null
    position?: GeoJson | null
    position_type: string | null
    access_point: PointGeoJson | null
    map_view: {
      west: number
      south: number
      east: number
      north: number
    } | null
  }
  order: number
  point_timezone?: string // after creation
  point_type: PointTypeSlug
  start_datetime: RoutePointDatetime
  time_zone: string
  start_time_offset_days?: RouteStartDateOffsetDays
}

export interface Payment {
  is_not_quote: boolean
  currency: CurrencySlug
  quote_is_vat: boolean | null
  quote: string | null
  universal_quote: string | null
  vat: string | null
  pure_quote: string | null
  quote_mileage: string | null
  mileage_format: MileageFormat | null
  is_prepay: boolean
  prepay_percent: number | null
  prepay_amount: string | null
  payment_condition: PaymentConditionSlug | null
  payment_period: number | null
  payment_method: PaymentMethodSlug | null
  bid_mode: BidMode
  bid_is_no_vat: boolean | null
  bid_is_vat: boolean | null
  bid_auction_step: string | null
  bid_is_auto_accept: boolean | null
  bid_is_not_unique: boolean | null
  is_quote_autochange: boolean | null
  quote_autochange: {
    is_increase: boolean | null
    step: string | null
    changed_at: string | null
    timeframe: number // ms
    quote_limit: string | null
  }
  bid_confirm_timeframe: number | null // ms
  allow_first_bid_no_step: boolean | null
  universal_currency_code: CurrencySlug // for new features, currently same as payment currency
}

export interface PublicationLifetime {
  is_first_bid: boolean | null
  lifetime: number // ms
  is_late_bid_prolong: boolean | null
  prolong_step: number // ms
  prolong_times: number
}

export interface CargoUnit {
  _id: ID
  name: string | null
  type: CargoTypeSlug | null
  quantity: number | null
  weight: number
  volume: number
  adr_class: AdrClassSlug | null
  length: number | null
  width: number | null
  height: number | null
  temperature_min_c: number | null
  temperature_max_c: number | null
}

export interface TransportationRequirement {
  shipping_mode: ShippingModeSlug | null
  type: TransportTypeSlug
  quantity: number | null
  body: TruckBodySlug[]
  equipment: ExtraEquipmentSlug[] | null
  is_trailer_interchange: boolean
}

export interface RouteWay {
  total_dur: number | null
  total_length: number | null
  way?: FlexiblePolyline | null
}

export interface AssigneeContact {
  id: Profile['id']
  contact_methods: {
    method: AssigneeContactMethod
    value: string
  }[]
}

interface GeoProcessState {
  status: GeoStateStatus
}

interface Organization {
  id: string
  name: string
}

export interface TransportInfo {
  type: TransportTypeSlug | null
  body: TruckBodySlug
  equipment: ExtraEquipmentSlug[]
  loading_type: LoadingTypeSlug[]
  shipping_mode: ShippingModeSlug[] | null
  is_trailer_interchange: boolean
  temperature_min_c: number | null
  temperature_max_c: number | null
  adr_class: AdrClassSlug[] | null
  available_weight: number | null
  available_volume: number | null
  length: number | null
  width: number | null
  height: number | null
}

export interface GetMyOffersRequest extends ExchangePagination {
  type?: OfferType
  status?: OfferStatus[]
  active_space_company_id?: Company['id'] | null
}

export interface GetTemplatesRequest {
  page?: number
  records?: number
  type?: OfferType
  active_space_company_id?: Company['id'] | null
}

export interface GetOffersResponse {
  result: Offer[]
  total: number
  count: number
  page: number
  count_by_status: OfferInStatusCount[]

  profiles?: Profile[]
  companies?: Company[]
}

export interface OfferInStatusCount {
  count: number
  status: OfferStatus
}

export interface GetPublicOffersRequest {
  offer_type: OfferType

  page?: number
  records?: number
  sort?: 'asc' | 'desc'

  start_point?: {
    osm_id: string
    osm_type: string
    radius: number | null
  }
  end_point?: {
    osm_id: string
    osm_type: string
    radius: number | null
  }

  shipping_mode?: string | null
  transport_body?: TruckBodySlug[] | null
  transport_type?: TransportTypeSlug[] | null
  weight_min?: number | null
  weight_max?: number | null
  volume_min?: number | null
  volume_max?: number | null
  adr_class?: AdrClassSlug[] | null
  type_exact?: boolean | null

  date_start?: string | null // '2016-01-02'
  date_end?: string | null // 2016-01-02
}

export interface GetPublicOfferRequest {
  id: Offer['_id']
  user_hash: string
}

export interface GetPublicOffersCompanyRequest extends ExchangePagination {
  offer_type?: OfferType
  companyId: string
}

export type GetTemplatesResponse = {
  count: number
  page: number
  total: number
  result: Array<Template>
}

export type CreateTemplateRequest = {
  name: string
  body: CreateTemplateBody
}

interface CreateTemplateBody extends CreateOfferRequestBase {
  company_id?: Company['id'] | null
}

export type Template = {
  id: ID
  name: string
  body: RecursivePartial<CreateOfferResponse>
}

export interface GetOfferResponse {
  result: Offer
  profiles: Profile[]
  companies: Company
}

export type GetOfferRouteResponse = RouteWay

export type GeoStateStatus = 'ok' | 'failed' | 'in_progress'

export interface GetOfferGeoStateResponse {
  status: GeoStateStatus
  error_msg?: string
}

export interface GetOfferMatchesRequest extends ExchangePagination {
  id: Offer['_id']

  exact: boolean
  polygon?: boolean
  way_polyline?: boolean
}

export interface GeoOfferMatchesResponse {
  count: number
  last_matched_at: string | null
  matches: Offer[]
  page: number
  total: number

  profiles?: Profile[]
  companies?: Company[]
}


/*
 * Type guards
 */
export const isCargoOffer = (offer: Pick<Offer, 'offer_type'>): offer is CargoOffer => {
  return offer.offer_type === OfferType.CARGO
}

export const isTransportOffer = (offer: Pick<Offer, 'offer_type'>): offer is TransportOffer => {
  return offer.offer_type === OfferType.TRANSPORT
}
/*
 * Type guards end
 */

export enum BidMode {
  AD = 'ad',
  BIDDING = 'bidding',
  AUCTION = 'auction',
  FIX = 'fix'
}

export enum BidStatus {
  CREATED = 'created',
  REJECTED = 'rejected',
  CANCELLED = 'cancelled',
  ACCEPTED = 'accepted',
  CONFIRMED = 'confirmed',
  WON = 'won'
}

export enum BidState {
  BEST = 'best',
  AMONG_BEST = 'among_best',
  AVERAGE = 'average',
  LOOSING = 'loosing',
  OUTBIDED = 'outbided '
}

export enum BidCategory {
  VAT = 'vat',
  NO_VAT = 'no_vat',
  MIXED = 'mixed'
}

export enum BidRejectReason {
  LOST_TO_ANOTHER = 'lost_to_another',
  CONFIRM_RUNOUT = 'confirm_runout',
  REJECT = 'reject',
  OFFER_CLOSED = 'offer_closed '
}

export interface BidsInfo {
  best_bid_vat: Bid
  best_bid_no_vat: Bid
  total_bids_vat: number
  total_bids_no_vat: number
}

export interface Bid {
  id: string
  assignee: Profile['id']
  origin_offer_id: Offer['_id']
  linked_offer_id: Offer['_id'] | null
  status: BidStatus
  state: BidState
  category: BidCategory
  company_id: string | null
  reject_reason: BidRejectReason | null
  quote: {
    is_vat: boolean
    current: CurrencySlug
    mileage_format: string | null
    comment: string | null
    price: string | null
    mileage_price: string | null
    universal_price: string
  }
  top_place: number | null
  is_archived: boolean
  rejected_at: string | null
  cancelled_at: string | null
  accepted_at: string | null
  confirmed_at: string | null
  won_at: string | null
  ends_at: string | null // indicates when bid must be cancelled (autocancel option)
  created_at: string
  updated_at: string
}
